/**
 * Reading the access token the app is holding. Decoding only — never
 * verification, which is the backend's job; these helpers answer "what kind of
 * session is this?" so the UI can route a dead session to the right place.
 */

export interface ICodeSession {
  /** The invitation uid — what the code screen needs to let the person back in. */
  uid: string;
  /** The single coleta this session may open (backend claim `codeAssignment`). */
  codeAssignment: number;
}

/**
 * Decodes a JWT payload without verifying it. An expired token still decodes,
 * which is the point: the claims are how we learn where to send someone whose
 * session just died.
 */
export function decodeJwtPayload(token: string | null): any | null {
  if (!token) return null;
  const segment = token.split(".")[1];
  if (!segment) return null;

  try {
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      window
        .atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * The stored session, if it is a code session — i.e. its token carries
 * `codeAssignment`. Normal logins answer null, which is what keeps every
 * code-specific branch off their path.
 */
export function getCodeSession(
  token: string | null = localStorage.getItem("AccessToken")
): ICodeSession | null {
  const payload = decodeJwtPayload(token);
  if (!payload || payload.codeAssignment === undefined) return null;

  const codeAssignment = Number(payload.codeAssignment);
  if (!payload.uid || !Number.isInteger(codeAssignment)) return null;

  return { uid: payload.uid, codeAssignment };
}

/**
 * Whether the stored token is past its `exp`. Used to tell an expired session
 * (send the person back to the code screen) from a live token the backend
 * refused on the merits — a scope violation, say — which is an error to show,
 * not a session to restart.
 */
export function isTokenExpired(
  token: string | null = localStorage.getItem("AccessToken")
): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== "number") return false;
  return payload.exp * 1000 <= Date.now();
}
